// ============================================================================
// NOTIFICATION TYPES
// ============================================================================

/**
 * Notification types supported by the system.
 */
export type NotificationType =
  | 'expiry_reminder'
  | 'low_stock_alert'
  | 'sharing_invitation'
  | 'system'
  | 'system_announcement';

/**
 * Notification data for expiry reminder notifications.
 */
export interface ExpiryReminderData {
  itemId: string;
  itemName: string;
  expiresAt: string; // ISO 8601 UTC
}

/**
 * Notification data for low stock alert notifications.
 */
export interface LowStockAlertData {
  itemId: string;
  itemName: string;
  currentQty: number;
}

/**
 * Notification data for sharing invitation notifications.
 */
export interface SharingInvitationData {
  householdId: string;
  householdName: string;
  inviterName: string;
}

/**
 * Notification data for system announcement notifications.
 * Links to an announcement for display in notification feed.
 */
export interface SystemAnnouncementData {
  announcementId: string;
}

/**
 * Fallback data for unknown notification types.
 */
export type UnknownNotificationData = Record<string, unknown>;

/**
 * Discriminated union for notification payloads.
 * The `type` field determines the shape of the `data` field.
 */
export type NotificationPayload =
  | { type: 'expiry_reminder'; data: ExpiryReminderData }
  | { type: 'low_stock_alert'; data: LowStockAlertData }
  | { type: 'sharing_invitation'; data: SharingInvitationData }
  | { type: 'system'; data: UnknownNotificationData }
  | { type: 'system_announcement'; data: SystemAnnouncementData }
  | { type: string; data: UnknownNotificationData }; // Unknown type fallback

/**
 * Announcement status for system_announcement notifications.
 * Computed based on announcement's activeFrom/activeUntil dates.
 */
export type AnnouncementNotificationStatus = 'active' | 'expired' | 'draft';

/**
 * Base notification response fields shared by all notification types.
 */
export interface NotificationBase {
  id: string;
  title: string;
  body: string | null;
  isRead: boolean;
  readAt: string | null; // ISO 8601 UTC or null
  createdAt: string; // ISO 8601 UTC
  /**
   * For system_announcement type only: the status of the linked announcement.
   * - active: Announcement is currently showing to users
   * - expired: Announcement's time window has passed
   * - draft: Announcement has no activeFrom date set
   */
  announcementStatus?: AnnouncementNotificationStatus;
}

/**
 * Full notification response type.
 * Combines base fields with the discriminated payload.
 */
export type NotificationResponse = NotificationBase & NotificationPayload;

/**
 * Paginated notifications response from the API.
 */
export interface PaginatedNotificationsResponse {
  items: NotificationResponse[];
  nextCursor: string | null;
  unreadCount: number;
  markAllCursor: string | null;
}

/**
 * Unread count response from the API.
 */
export interface UnreadCountResponse {
  count: number;
}

/**
 * Mark all as read response from the API.
 */
export interface MarkAllAsReadResponse {
  updatedCount: number;
}

/**
 * Query parameters for listing notifications.
 */
export interface NotificationQueryParams {
  limit?: number;
  cursor?: string;
  type?: NotificationType;
  isRead?: boolean;
}

/**
 * Validates that notification data matches the expected shape for its type.
 * Used for write-time validation when creating notifications.
 *
 * @param type - The notification type
 * @param data - The notification data to validate
 * @returns True if valid, false otherwise
 */
export function validateNotificationData(type: NotificationType | string, data: unknown): boolean {
  if (!data || typeof data !== 'object') {
    return false;
  }

  const d = data as Record<string, unknown>;

  switch (type) {
    case 'expiry_reminder':
      return (
        typeof d.itemId === 'string' &&
        typeof d.itemName === 'string' &&
        typeof d.expiresAt === 'string'
      );

    case 'low_stock_alert':
      return (
        typeof d.itemId === 'string' &&
        typeof d.itemName === 'string' &&
        typeof d.currentQty === 'number'
      );

    case 'sharing_invitation':
      return (
        typeof d.householdId === 'string' &&
        typeof d.householdName === 'string' &&
        typeof d.inviterName === 'string'
      );

    case 'system_announcement':
      return typeof d.announcementId === 'string';

    case 'system':
      // System notifications accept any data
      return true;

    default:
      // Unknown types - allow for forward compatibility
      return true;
  }
}

/**
 * Type guard to check if a notification is a known type.
 */
export function isKnownNotificationType(type: string): type is NotificationType {
  return [
    'expiry_reminder',
    'low_stock_alert',
    'sharing_invitation',
    'system',
    'system_announcement',
  ].includes(type);
}

/**
 * Gets the deep link path for a notification based on its type.
 * Returns null if the notification type doesn't support deep linking.
 *
 * @param notification - The notification to get the deep link for
 * @returns The deep link path or null
 */
export function getNotificationDeepLink(notification: NotificationResponse): string | null {
  switch (notification.type) {
    case 'expiry_reminder':
    case 'low_stock_alert': {
      const itemId = (notification.data as ExpiryReminderData | LowStockAlertData)?.itemId;
      return itemId ? `/items/${itemId}` : null;
    }

    case 'sharing_invitation': {
      const householdId = (notification.data as SharingInvitationData)?.householdId;
      return householdId ? `/households/${householdId}` : null;
    }

    case 'system_announcement':
      // System announcements don't deep link - clicking just marks as read
      return null;

    default:
      // Unknown types - no deep link, just mark as read
      return null;
  }
}
